import Router from 'next/router';

import useWorkspaceUrl from '@hooks/use-workspace-url';
import { Box, Button, Heading, Text } from '@ui-lib';

import * as styles from './NoCardsMessage.css';
import { useUser } from '@context/user-context';
import { isMemberCardCreateEnabled } from '@modules/user-helpers';

type NoCardsMessageProps = {
  route: 'personal' | 'team' | 'templates';
};

export function NoCardsMessage({ route }: NoCardsMessageProps): JSX.Element {
  const newCardUrl = useWorkspaceUrl(`cards/${route}/new`);
  const user = useUser();

  // team members with 'disable_member_card_creation' set to true on workspace tags should not be allowed to create personal cards
  const memberCanCreateCard = isMemberCardCreateEnabled(user);

  if (route === 'templates') {
    return (
      <Box display="flex" width="full" justifyContent="center" alignItems="center" padding="large">
        <Box
          className={styles.createCardMessageContent}
          display="flex"
          flexDirection="column"
          alignItems="center"
          gap="large"
        >
          <Heading level="2">You don&apos;t have any Templates!</Heading>

          <Text tone="neutral" align="center">
            Templates allow you to quickly create consistently styled cards for you and your team members.
            Click the button below (or the <span className={styles.createCardPlus}>+</span> in the sidebar) to
            create a template.
          </Text>

          <Button type="button" variant="solid" size="regular" onClick={() => Router.push(newCardUrl)}>
            Create a new Template
          </Button>
        </Box>
      </Box>
    );
  }
  if (route === 'team') {
    return (
      <Box display="flex" width="full" justifyContent="center" alignItems="center" padding="large">
        <Box
          className={styles.createCardMessageContent}
          display="flex"
          flexDirection="column"
          alignItems="center"
          gap="large"
        >
          <Heading level="2">You don&apos;t have any cards!</Heading>

          <Text tone="neutral" align="center">
            Click the button below to create a team card.
          </Text>

          <Button type="button" variant="solid" size="regular" onClick={() => Router.push(newCardUrl)}>
            Create a new Card
          </Button>
        </Box>
      </Box>
    );
  }
  if (route === 'personal') {
    return (
      <Box display="flex" width="full" justifyContent="center" alignItems="center" padding="large">
        <Box
          className={styles.createCardMessageContent}
          display="flex"
          flexDirection="column"
          alignItems="center"
          gap="large"
        >
          {memberCanCreateCard ? (
            <>
              <Heading level="2">You don&apos;t have any cards!</Heading>

              <Text tone="neutral" align="center">
                Click the button below (or the <span className={styles.createCardPlus}>+</span> in the
                sidebar) to create a personal card.
              </Text>

              <Button type="button" variant="solid" size="regular" onClick={() => Router.push(newCardUrl)}>
                Create a new Card
              </Button>
            </>
          ) : (
            <>
              <Heading align="center" level="2">
                Your company hasn&apos;t assigned a card to you yet.
              </Heading>

              <Text tone="neutral" align="center">
                Contact your admin to get a personalised Blinq card.
              </Text>
            </>
          )}
        </Box>
      </Box>
    );
  }

  return <></>;
}
